import { lazy, ComponentType } from 'react';
import { LEARNER_PERSIAN_FONT_OPTIONS } from '../constants/learnerPersianFonts';
import { isModuleLoadError, reloadOnceForStaleChunk } from '../utils/moduleLoadError';

export interface PerformanceMetrics {
  componentRenderTime: number;
  apiResponseTime: number;
  fontLoadTime: number;
  bundleSize: number;
  memoryUsage: number;
  cacheHitRate: number;
}

export interface LazyComponentConfig {
  fallback?: ComponentType;
  preload?: boolean;
}

class PerformanceService {
  private metrics: Map<string, number[]> = new Map();
  private observers: Map<string, PerformanceObserver> = new Map();

  constructor() {
    this.initializePerformanceObservers();
  }

  /**
   * Create lazy-loaded component with performance tracking.
   * Do not race the import against a short timeout: background tabs freeze
   * timers, so a 10s cap fires when the user returns and crashes the page.
   */
  createLazyComponent<T extends ComponentType<any>>(
    importFn: () => Promise<{ default: T }>,
    componentName: string,
    config: LazyComponentConfig = {}
  ): ComponentType<any> {
    const loadWithRetry = async (): Promise<{ default: T }> => {
      const retries = 2;
      let lastError: unknown;

      for (let attempt = 0; attempt <= retries; attempt++) {
        const startTime = performance.now();
        try {
          const module = await importFn();
          this.recordMetric(`component_load_${componentName}`, performance.now() - startTime);
          return module;
        } catch (error) {
          lastError = error;
          console.warn(`Failed to load component ${componentName} (attempt ${attempt + 1}):`, error);
          if (attempt < retries) {
            await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
          }
        }
      }

      if (isModuleLoadError(lastError) && reloadOnceForStaleChunk()) {
        return new Promise(() => {
          /* page is reloading */
        });
      }

      console.error(`Failed to load component ${componentName}:`, lastError);
      throw lastError instanceof Error ? lastError : new Error(String(lastError));
    };

    const LazyComponent = lazy(loadWithRetry);

    if (config.preload) {
      setTimeout(() => {
        importFn().catch(console.error);
      }, 100);
    }

    return LazyComponent;
  }

  /**
   * Warm up bundled Persian/Arabic UI fonts (@fontsource). Avoids legacy /fonts/*.woff2 URLs
   * (they 404 to index.html in dev, which triggers OTS "invalid sfntVersion" decode errors).
   */
  async optimizePersianFontLoading(): Promise<void> {
    const startTime = performance.now();

    try {
      if ('fonts' in document) {
        const bundled = LEARNER_PERSIAN_FONT_OPTIONS.filter((o): o is (typeof o & { cssFamily: string }) => !!o.cssFamily);
        await Promise.all(
          bundled.map((o) => document.fonts.load(`400 16px "${o.cssFamily}"`).catch(() => undefined))
        );
      }

      const loadTime = performance.now() - startTime;
      this.recordMetric('persian_fonts_load', loadTime);

      this.applyFontDisplaySwap();
    } catch (error) {
      console.error('Persian font optimization failed:', error);
    }
  }

  /**
   * Apply font-display: swap for better performance
   */
  private applyFontDisplaySwap(): void {
    // Temporarily disabled to prevent PostCSS errors
    // const style = document.createElement('style');
    // style.textContent = `
    //   @font-face {
    //     font-family: 'Vazir';
    //     font-display: swap;
    //   }
    //   @font-face {
    //     font-family: 'Shabnam';
    //     font-display: swap;
    //   }
    //   @font-face {
    //     font-family: 'Samim';
    //     font-display: swap;
    //   }
    // `;
    // document.head.appendChild(style);
  }

  /**
   * Optimize bundle splitting
   */
  getBundleSplitConfig() {
    return {
      // Admin interface bundle
      admin: () => import('../components/admin/AdminRouter'),
      
      // Learner interface bundles
      grammar: () => import('../components/Grammar'),
      quiz: () => import('../components/Quiz'),
      pronunciation: () => import('../components/Pronunciation'),
      dashboard: () => import('../components/Dashboard'),
      
      // Shared components bundle
      shared: () => import('../components/shared'),
      
      // Persian language bundle
      persian: () => import('../services/persianLanguageService'),
      
      // Audio features bundle
      audio: () => import('../services/audioService')
    };
  }

  /**
   * Record performance metric
   */
  recordMetric(name: string, value: number): void {
    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }
    
    const values = this.metrics.get(name)!;
    values.push(value);
    
    // Keep only last 100 measurements
    if (values.length > 100) {
      values.shift();
    }
  }

  /**
   * Get performance metrics
   */
  getMetrics(): Record<string, PerformanceMetrics> {
    const result: Record<string, any> = {};
    
    for (const [name, values] of this.metrics.entries()) {
      if (values.length > 0) {
        result[name] = {
          average: values.reduce((a, b) => a + b, 0) / values.length,
          min: Math.min(...values),
          max: Math.max(...values),
          count: values.length,
          latest: values[values.length - 1]
        };
      }
    }
    
    return result;
  }

  /**
   * Measure component render time
   */
  measureComponentRender<T>(
    componentName: string,
    renderFn: () => T
  ): T {
    const startTime = performance.now();
    const result = renderFn();
    const renderTime = performance.now() - startTime;
    
    this.recordMetric(`render_${componentName}`, renderTime);
    
    return result;
  }

  /**
   * Measure API call performance
   */
  async measureApiCall<T>(
    apiName: string,
    apiCall: () => Promise<T>
  ): Promise<T> {
    const startTime = performance.now();
    
    try {
      const result = await apiCall();
      const responseTime = performance.now() - startTime;
      this.recordMetric(`api_${apiName}`, responseTime);
      return result;
    } catch (error) {
      const errorTime = performance.now() - startTime;
      this.recordMetric(`api_error_${apiName}`, errorTime);
      throw error;
    }
  }

  /**
   * Initialize performance observers
   */
  private initializePerformanceObservers(): void {
    // Observe navigation timing
    if ('PerformanceObserver' in window) {
      try {
        const navObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationTiming;
              this.recordMetric('page_load', navEntry.loadEventEnd - navEntry.fetchStart);
              this.recordMetric('dom_content_loaded', navEntry.domContentLoadedEventEnd - navEntry.fetchStart);
            }
          }
        });
        
        navObserver.observe({ entryTypes: ['navigation'] });
        this.observers.set('navigation', navObserver);
      } catch (error) {
        console.warn('Navigation observer not supported:', error);
      }

      // Observe resource timing
      try {
        const resourceObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name.includes('font')) {
              this.recordMetric('font_load', entry.duration);
            } else if (entry.name.includes('api')) {
              this.recordMetric('api_resource', entry.duration);
            }
          }
        });
        
        resourceObserver.observe({ entryTypes: ['resource'] });
        this.observers.set('resource', resourceObserver);
      } catch (error) {
        console.warn('Resource observer not supported:', error);
      }
    }
  }

  /**
   * Get memory usage information
   */
  getMemoryUsage(): any {
    if ('memory' in performance) {
      return {
        usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
        totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
        jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit
      };
    }
    return null;
  }

  /**
   * Optimize images for Persian content
   */
  optimizeImages(): void {
    // Add intersection observer for lazy loading images
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              imageObserver.unobserve(img);
            }
          }
        });
      });

      // Observe all images with data-src attribute
      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  }

  /**
   * Clean up performance observers
   */
  destroy(): void {
    for (const observer of this.observers.values()) {
      observer.disconnect();
    }
    this.observers.clear();
    this.metrics.clear();
  }
}

// Create singleton instance
export const performanceService = new PerformanceService();

export default PerformanceService;